/**
 * HubHeader + PillTabs — moodata-style page pattern สำหรับ hub ที่มีแท็บ.
 * หัวข้อ + chip บทบาท + subtitle + ปุ่ม action (เลือกใส่) + แท็บแบบ pill.
 * ใช้ร่วมทุก hub (พนักงาน/เวลา/ค่าจ้าง/รอบเงินเดือน/อนุมัติ) ให้ทรงเดียวกัน.
 */
import type { ReactNode } from 'react';

export type AppRole = 'OWNER' | 'HR' | 'MANAGER' | 'SUPERVISOR' | 'EMPLOYEE' | string;

export function roleLabelTh(role: AppRole): string {
  switch (role) {
    case 'OWNER': return 'เจ้าของ';
    case 'HR': return 'ฝ่ายบุคคล';
    case 'MANAGER': return 'ผู้จัดการ';
    case 'SUPERVISOR': return 'หัวหน้างาน';
    case 'EMPLOYEE': return 'พนักงาน';
    default: return String(role);
  }
}

export function HubHeader({
  section,
  title,
  roleLabel,
  // เดิมใส่คำโปรยนี้ให้ทุก hub เป็นค่าตั้งต้น — ไม่บอกอะไรผู้ใช้ ทำหัวหน้าดูแน่น (กวาดจาก pimtanan 28 ก.ย. 69)
  subtitle,
  action,
}: {
  section: string;
  title: string;
  roleLabel?: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="min-w-0">
        <div className="text-xs text-gray-400">{section}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-xl font-display font-semibold text-gray-900">{title}</h2>
          {roleLabel && <span className="badge bg-slate-100 text-slate-700">{roleLabel}</span>}
        </div>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function PillTabs<T extends string>({
  tabs,
  active,
  onChange,
  badges,
}: {
  tabs: Array<{ key: T; label: string }>;
  active: T;
  onChange: (k: T) => void;
  /** badge count ต่อ tab (เลือกใส่) */
  badges?: Partial<Record<T, number>>;
}) {
  return (
    <div className="flex gap-2 mb-4 overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 pb-1">
      {tabs.map((t) => {
        const isActive = active === t.key;
        const count = badges?.[t.key] ?? 0;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition ${
              isActive ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t.label}
            {count > 0 && (
              <span className={`inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full text-[10px] font-semibold ${
                isActive ? 'bg-white/25 text-white' : 'bg-rose-500 text-white'
              }`}>
                {count > 99 ? '99+' : count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
